/** Canonical event ids and platform names. Ids are stable so retries do not double-count. */

export const CANONICAL_EVENTS = [
  'page_view',
  'view_tool',
  'sign_up',
  'first_tool_use',
  'purchase',
] as const;

export type CanonicalEvent = (typeof CANONICAL_EVENTS)[number];

const BLOCKED_PARAM_KEYS = new Set([
  'email',
  'user_email',
  'em',
  'phone',
  'name',
  'first_name',
  'last_name',
  'external_id',
  'token',
  'access_token',
]);

export type EventParams = Record<string, string | number>;

export function sanitizeEventParams(input: Record<string, unknown> | null | undefined): EventParams {
  const out: EventParams = {};
  if (!input) return out;
  for (const [key, value] of Object.entries(input)) {
    const normalised = key.trim().toLowerCase();
    if (!normalised || BLOCKED_PARAM_KEYS.has(normalised)) continue;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[\u0000-\u001F\u007F]/g, '').trim().slice(0, 200);
      if (!cleaned || cleaned.includes('@')) continue;
      out[key] = cleaned;
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = value;
    }
  }
  return out;
}

function safeToken(value: string, max: number): string {
  return value.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, max);
}

export function pageViewEventId(sessionId: string, path: string): string {
  const session = safeToken(sessionId, 32);
  const pathToken = safeToken(path || 'root', 80) || 'root';
  if (!session) throw new Error('invalid session');
  return `pv_${session}_${pathToken}`;
}

export function viewToolEventId(app: string, sessionId: string): string {
  const appToken = safeToken(app, 40);
  const session = safeToken(sessionId, 32);
  if (!appToken || !session) throw new Error('invalid view');
  return `vt_${appToken}_${session}`;
}

export function signupEventId(userId: string): string {
  const id = userId.trim().toLowerCase();
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(id)) {
    throw new Error('invalid user');
  }
  return `signup_${id}`;
}

export function firstToolEventId(userId: string): string {
  const id = userId.trim().toLowerCase();
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(id)) {
    throw new Error('invalid user');
  }
  return `first_tool_${id}`;
}

export function purchaseEventId(sessionId: string): string {
  const clean = safeToken(sessionId, 80);
  if (!clean.startsWith('cs_')) throw new Error('invalid session');
  return `purchase_${clean}`;
}

export interface PlatformEventMap {
  ga4Name: string;
  metaName: string;
  metaCustom: boolean;
  adsSlot: 'signup' | 'first_tool' | 'purchase' | null;
  linkedinSlot: 'signup' | 'first_tool' | 'purchase' | null;
}

const DUPLICATE_CONVERSION_NAMES = new Set([
  'signup',
  'registrationcomplete',
  'completeregistration',
  'purchase',
  'subscribe',
  'conversion',
  'firsttooluse',
  'firstsuccessfulcleanup',
  'firstgeneration',
]);

/** True when a direct gtag/fbq call would repeat a canonical conversion. */
export function isDuplicateConversionEvent(name: string): boolean {
  const normalised = name.trim().toLowerCase().replace(/[\s_-]+/g, '');
  return DUPLICATE_CONVERSION_NAMES.has(normalised);
}

export function mapCanonicalEvent(event: CanonicalEvent): PlatformEventMap {
  switch (event) {
    case 'page_view':
      return { ga4Name: 'page_view', metaName: 'PageView', metaCustom: false, adsSlot: null, linkedinSlot: null };
    case 'view_tool':
      return { ga4Name: 'view_tool', metaName: 'ViewContent', metaCustom: false, adsSlot: null, linkedinSlot: null };
    case 'sign_up':
      return { ga4Name: 'sign_up', metaName: 'CompleteRegistration', metaCustom: false, adsSlot: 'signup', linkedinSlot: 'signup' };
    case 'first_tool_use':
      return { ga4Name: 'first_tool_use', metaName: 'FirstToolUse', metaCustom: true, adsSlot: 'first_tool', linkedinSlot: 'first_tool' };
    case 'purchase':
      return { ga4Name: 'purchase', metaName: 'Purchase', metaCustom: false, adsSlot: 'purchase', linkedinSlot: 'purchase' };
    default: {
      const neverEvent: never = event;
      throw new Error(`unknown event ${neverEvent}`);
    }
  }
}

export interface FiredEventStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const FIRED_KEY = 'rehub_fired_events';

/** Returns true only the first time this id is seen in the store. */
export function rememberEvent(storage: FiredEventStore, eventId: string): boolean {
  let seen: string[] = [];
  try {
    const raw = storage.getItem(FIRED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) seen = parsed.filter((item) => typeof item === 'string');
  } catch {
    seen = [];
  }
  if (seen.includes(eventId)) return false;
  seen.push(eventId);
  try {
    storage.setItem(FIRED_KEY, JSON.stringify(seen.slice(-200)));
  } catch {
    /* still treat this call as the first fire for the current render */
  }
  return true;
}

export function shouldFirePurchasePixel(input: {
  claimed: boolean;
  cutover: boolean;
  advertisingConsent: boolean;
}): boolean {
  if (!input.claimed) return false;
  if (input.cutover) return input.advertisingConsent;
  return true;
}

/**
 * The browser may send a conversion only for the first successful claim.
 * already_sent, not_found, and a failed signup (no row) do not fire.
 */
export function shouldFireClaimedConversion(row: Record<string, unknown> | null | undefined): boolean {
  return Boolean(row && row.claimed === true && typeof row.event_id === 'string' && row.event_id);
}

/** Auth callback pages redirect away. Claiming there can mark the row sent before gtag runs. */
export function isAuthCallbackLocation(href: string): boolean {
  try {
    const url = new URL(href, 'https://rehub.studio');
    const path = url.pathname.replace(/\/$/, '') || '/';
    if (path === '/auth/callback') return true;
    const hashPath = url.hash.replace(/^#\/?/, '').split('?')[0].replace(/\/$/, '');
    return hashPath === 'auth/callback' || hashPath === 'auth-callback';
  } catch {
    return false;
  }
}
